<script setup lang="ts">
import { Check, X } from "@lucide/vue";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { Button, Input } from "@/shared/ui";

import { useKeyboardAwareFocus } from "../model/useKeyboardAwareFocus";
import { useServicesCatalogStore } from "../model/store";

const props = defineProps<{
  categoryId?: string | null;
  ariaLabel?: string;
}>();

const { t } = useI18n();
const store = useServicesCatalogStore();

const text = ref("");
const inputRef = ref<{ $el: HTMLInputElement } | null>(null);

const { onFocusInput, onBlurInput } = useKeyboardAwareFocus();

const canSubmit = computed(() => text.value.trim().length > 0);

const placeholder = computed(() =>
  store.hasSavedRows ? t("services.draft.placeholderNext") : t("services.draft.placeholder"),
);

onMounted(() => {
  inputRef.value?.$el.focus();
});

function submit(): void {
  const value = text.value.trim();
  if (!value) {
    return;
  }
  store.create(value, props.categoryId);
  text.value = "";
  inputRef.value?.$el.focus();
}

function cancel(): void {
  store.closeDraft();
}

function focus(): void {
  inputRef.value?.$el.focus();
}

defineExpose({ focus });
</script>

<template>
  <div class="flex items-center gap-2 px-1 py-1.5">
    <Input
      ref="inputRef"
      v-model="text"
      class="flex-1"
      data-testid="service-draft-input"
      :placeholder="placeholder"
      :aria-label="props.ariaLabel ?? t('services.draft.placeholder')"
      autocomplete="off"
      enterkeyhint="done"
      @focus="onFocusInput"
      @blur="onBlurInput"
      @keydown.enter.prevent="submit"
      @keydown.esc.prevent="cancel"
    />
    <Button
      variant="ghost"
      size="icon"
      class="max-sm:size-11"
      :disabled="!canSubmit"
      :aria-label="t('services.actions.confirm')"
      @click="submit"
    >
      <Check :size="20" aria-hidden="true" />
    </Button>
    <Button
      variant="ghost"
      size="icon"
      class="max-sm:size-11"
      :aria-label="t('services.actions.cancel')"
      @click="cancel"
    >
      <X :size="20" aria-hidden="true" />
    </Button>
  </div>
</template>
